import { yupResolver } from "@hookform/resolvers/yup"
import PropTypes from "prop-types"
import { useForm, Controller } from "react-hook-form"
import isEmail from "validator/lib/isEmail"
import * as Yup from "yup"

import { history } from "app-store-v2"
import { openModal } from "components/modal"
import Button from "components/ui/button"
import FloatInput from "components/ui/float-input"
import Image from "components/ui/image"
import Switch from "components/ui/switch"
import withAuth from "hoc/account/with-auth"

import { Footnote, HaveAccount, LogoContainer, Separator } from "./components"

const schema = Yup.object().shape({
  email: Yup.string()
    .required("Please enter a valid email address.")
    .test("test-email", "Please enter a valid email address.", (value) =>
      isEmail(value || "")
    ),
  password: Yup.string().required("Please enter your password."),
})

const LoginForm = ({ doFacebookLogin, regularLogin, setModalContent }) => {
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
      remember_me: true,
    },
    mode: "onBlur",
    resolver: yupResolver(schema),
  })

  const onSubmit = async (data) => {
    try {
      await new Promise((res, rej) => {
        regularLogin(data, {
          fromModal: !!setModalContent,
          onError: (err) => rej(err),
          onSuccess: () => res(),
        })
      })
    } catch (err) {
      setError("root.loginError", { message: err })
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {errors?.root?.loginError && (
        <p className="text-error mb-16">{errors.root.loginError.message}</p>
      )}
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState: { error } }) => (
          <FloatInput
            autoComplete="email"
            containerClassName="mb-16 text-left"
            data-cy="login-email-input"
            error={error}
            id="email"
            label="Email"
            placeholder="Enter Your Email"
            type="email"
            {...field}
          />
        )}
        rules={{ required: true }}
      />
      <Controller
        control={control}
        name="password"
        render={({ field, fieldState: { error } }) => (
          <FloatInput
            autoComplete="current-password"
            containerClassName="mb-16 text-left"
            data-cy="login-password-input"
            error={error}
            id="password"
            label="Password"
            placeholder="Enter Your Password"
            type="password"
            {...field}
          />
        )}
        rules={{ required: true }}
      />
      <div className="mb-24 flex items-center justify-between pt-8 text-left">
        <Controller
          control={control}
          name="remember_me"
          render={({ field: { value, ...props }, fieldState: { error } }) => (
            <Switch checked={value} error={error} id="remember_me" {...props}>
              <p className="text-12 leading-20 tracking-tigher font-medium">
                Remember Me
              </p>
            </Switch>
          )}
          rules={{ required: true }}
        />
        <Button
          className="text-12"
          data-cy="login-forgot-password"
          onClick={() => openModal("#forgot-password")}
          text
          underline
          variant="secondary"
        >
          Forgot your password?
        </Button>
      </div>
      <Button
        className="mb-16 w-full"
        data-cy="login-sign-in-button"
        loading={isSubmitting}
        type="submit"
        variant="secondary"
      >
        Sign In
      </Button>
      <Separator className="mb-16">or sign up using</Separator>
      <LogoContainer>
        <Button onClick={(e) => doFacebookLogin(e)}>
          <Image
            alt="Sign in with Facebook"
            className="w-auto"
            height="310"
            src={require("images/icons/facebook.png")}
            style={{ height: "30px" }}
            width="309"
          />
        </Button>
      </LogoContainer>
      <HaveAccount className="mb-16">Don&apos;t have an account?</HaveAccount>
      <Button
        className="mb-16 w-full border border-black"
        onClick={() =>
          setModalContent ? setModalContent("#signup") : history.push("/signup")
        }
        variant="tertiary"
      >
        Create Account
      </Button>
      <Footnote>
        Note: Creating an account is not required to purchase from{" "}
        {sl.config.site_title}.
      </Footnote>
    </form>
  )
}

LoginForm.propTypes = {
  doFacebookLogin: PropTypes.func,
  regularLogin: PropTypes.func,
  setModalContent: PropTypes.func,
}

export default withAuth(LoginForm)
